import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const BusinessDashboardNav = ({ 
  userRole = 'event-manager', // 'event-manager' | 'admin'
  isCollapsed = false,
  onToggleCollapse = () => {},
  user = { name: 'User', role: 'Event Manager' }
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/home');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const eventManagerNavItems = [
    {
      label: 'Dashboard',
      path: '/event-manager-dashboard',
      icon: 'BarChart3',
      description: 'Overview & Analytics'
    },
    {
      label: 'Events',
      path: '/events',
      icon: 'Calendar',
      description: 'Manage Events'
    },
    {
      label: 'Tickets',
      path: '/tickets',
      icon: 'Ticket',
      description: 'Ticket Management'
    },
    {
      label: 'Sales',
      path: '/sales',
      icon: 'TrendingUp',
      description: 'Sales Reports'
    },
    {
      label: 'Scanner',
      path: '/event-scanner',
      icon: 'QrCode',
      description: 'Scan Tickets'
    }
  ];

  const adminNavItems = [
    {
      label: 'Admin Portal',
      path: '/admin-portal-dashboard',
      icon: 'Shield',
      description: 'System Overview'
    },
    {
      label: 'Users',
      path: '/admin/users',
      icon: 'Users',
      description: 'User Management'
    },
    {
      label: 'Events',
      path: '/admin/events',
      icon: 'Calendar',
      description: 'Event Oversight'
    },
    {
      label: 'Financial',
      path: '/admin/financial',
      icon: 'DollarSign',
      description: 'Financial Reports'
    },
    {
      label: 'Settings',
      path: '/admin/settings',
      icon: 'Settings',
      description: 'System Settings'
    }
  ];

  const navItems = userRole === 'admin' ? adminNavItems : eventManagerNavItems;

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col lg:z-[1000] transition-all duration-300 ${
        isCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className="flex flex-col flex-1 min-h-0 bg-card border-r border-border shadow-elevation-2">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <button 
                onClick={handleLogoClick}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Ticket" size={20} color="white" />
                </div>
                <span className="text-lg font-semibold text-foreground">Tixful</span>
              </button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
              className="text-muted-foreground hover:text-foreground"
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  location?.pathname === item?.path
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-foreground'
                }`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  className={`flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}
                />
                {!isCollapsed && (
                  <div className="flex-1 text-left">
                    <div className="font-medium">{item?.label}</div>
                    <div className="text-xs opacity-75">{item?.description}</div>
                  </div>
                )}
              </button>
            ))}
          </nav>
        </div>
      </aside>
      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000] bg-card border-t border-border shadow-elevation-3">
        <div className="flex items-center justify-around py-2">
          {navItems?.slice(0, 4)?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`flex flex-col items-center px-3 py-2 text-xs font-medium transition-colors ${
                location?.pathname === item?.path
                  ? 'text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={item?.icon} size={20} className="mb-1" />
              <span>{item?.label}</span>
            </button>
          ))}
          
          {navItems?.length > 4 && (
            <button
              onClick={toggleMobileMenu}
              className="flex flex-col items-center px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="MoreHorizontal" size={20} className="mb-1" />
              <span>More</span>
            </button>
          )}
        </div>
      </div>
      {/* Mobile Overflow Menu */}
      {isMobileMenuOpen && navItems?.length > 4 && (
        <div className="lg:hidden fixed inset-0 z-[1100] bg-black bg-opacity-50" onClick={toggleMobileMenu}>
          <div className="absolute bottom-16 left-4 right-4 bg-popover border border-border rounded-lg shadow-elevation-4">
            <div className="p-4">
              <h3 className="text-sm font-medium text-popover-foreground mb-3">More Options</h3>
              <div className="space-y-2">
                {navItems?.slice(4)?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location?.pathname === item?.path
                        ? 'bg-primary text-primary-foreground'
                        : 'text-popover-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={18} className="mr-3" />
                    <div className="text-left">
                      <div>{item?.label}</div>
                      <div className="text-xs opacity-75">{item?.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-[999] bg-card border-b border-border shadow-elevation-1">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={handleLogoClick}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Ticket" size={20} color="white" />
            </div>
            <span className="text-lg font-semibold text-foreground">Tixful</span>
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
              <Icon name="User" size={16} color="white" />
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default BusinessDashboardNav;